import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SchoolControllerApiService } from '@lx/shared/api';
import { getCompanyHeader } from '@lx/shared/tokens';
import { getLocale } from '@lx/shared/i18n';
import { Memoize } from '@lx/shared/utils-http';
import { CountryDetectorService } from '@lx/quote-tool/shared/services';

/**
 * Service to resolve the appropriate online school code for the user.
 *
 * Online schools may vary by residence country, so this service determines
 * which online school to use based on user context.
 *
 * This service also provides access to all schools for deep link processing.
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineSchoolResolverService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly schoolControllerService = inject(SchoolControllerApiService);
  private readonly countryDetectorService = inject(CountryDetectorService);

  /**
   * Get the online school code for the user's detected country of residence.
   *
   * The country is automatically detected from the browser's timezone.
   * If no country can be detected or no online school is found for the detected country, an error is thrown.
   *
   * @returns Observable of online school code
   */
  @Memoize({ tags: ['getOnlineSchoolCode'] })
  getOnlineSchoolCode(): Observable<string> {
    const countryCode = this.countryDetectorService.detectCountryCode();
    if (!countryCode) {
      throw new Error('Could not detect country from browser timezone, using default online school');
    }

    return this.schoolControllerService.findOnlineSchools(this.locale, this.companyHeader, countryCode).pipe(
      map((onlineSchools) => {
        const schoolCode = onlineSchools?.onlineSchoolCodes?.[0];

        if (!schoolCode) {
          throw new Error(`No online school found for country ${countryCode}, using default`);
        }

        return schoolCode;
      }),
      catchError((error) => {
        throw new Error(`Failed to fetch online school for country ${countryCode}: ${error.message}`);
      }),
    );
  }
}
